import { Component, inject, OnInit, ViewChild, EventEmitter, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { ProjectService } from '../../../services/project.service';
import { MatIconModule } from '@angular/material/icon';
import { vm_category } from '../../../models/view-models/vm-category.model';
import { ApiService } from '../../../services/api.service';
import { ViewModelMapperService } from '../../../services/view-model.mapper.service';
import { IconComponent } from '../../../ui-component-library/core/icon.component/icon.component';
import { CommonServices } from '../../../services/common.services';
import { MatTooltipModule } from '@angular/material/tooltip';
import { dm_projectType } from '../../../models/data-models/dm_projectType.mdoel';
import { dm_category } from '../../../models/data-models/dm_category.model';
import { vm_product } from '../../../models/view-models/vm_product.model';
import { EllipsisTooltipDirective } from '../../../directives/ellipsis-tooltip.directive';
import { dm_product } from '../../../models/data-models/dm_product.model';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-create-project-step-1',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ScrollingModule,
    MatStepperModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatButtonModule,
    MatTooltipModule,
    FormsModule,
    MatIconModule,
    IconComponent,
    EllipsisTooltipDirective,
  ],
  templateUrl: './create-project-step-1.component.html',
  styleUrl: './create-project-step-1.component.css',
})
export class CreateObjectiveStep1Component implements OnInit {
  @Output() createTestProduct = new EventEmitter<void>();
  private service = inject(ProjectService);
  step1Form: FormGroup;
  categories: vm_category[] = [];
  projectTypes: dm_projectType[] = [];
  products: vm_product[] = [];
  productSearchTerm = '';
  isEditMode = false;
  isDataLoaded = false;
  private lockedProductIds = new Set<string>();

  visibleProducts: vm_product[] = [];
  private productsRaw: dm_product[] = [];
  private selectedProductIds = new Set<string>();
  private linkedProductIdsForCreate = new Set<string>();
  private mappedProductsById = new Map<string, vm_product>();

  private readonly productPageSize = 200;
  private productPage = 0;
  hasMoreProducts = false;
  isProductSearchLoading = false;
  private isProductPageLoading = false;
  private productSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();
  @ViewChild(CdkVirtualScrollViewport) productsViewport?: CdkVirtualScrollViewport;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private vmService: ViewModelMapperService,
    private projectSrvc: ProjectService,
    private commonService: CommonServices
  ) {
    this.apiService.dataChangeEvent$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.dataLoaded) {
        this.loadInitialData();
      }
    });
  }

  ngOnInit() {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    if (this.productSearchTimer) {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadInitialData() {
    if (this.isDataLoaded && this.productsRaw.length > 0 && this.categories.length > 0) return;

    this.categories =
      this.vmService
        .mapCategoriesToViewModel(this.apiService.categories, this.apiService.icons)
        ?.map((cat) => ({ ...cat, selected: false })) || [];

    this.projectTypes = this.apiService.projectTypes ?? [];

    this.productsRaw = this.sortRawProductsByModifiedDateDesc(this.apiService.products ?? []);
    this.linkedProductIdsForCreate = new Set(
      (this.apiService.productProjects ?? []).map((pp: any) => String(pp?.productId ?? '').trim()).filter(Boolean)
    );

    this.buildForm();
    this.isDataLoaded = true;
    this.loadExistingData();
    this.resetProductPaging();
  }

  get formData() {
    return this.step1Form?.value;
  }

  buildForm() {
    this.step1Form = this.fb.group({
      categories: this.fb.array(
        this.categories.map(() => false),
        [this.minSelectedCheckboxes(1)]
      ),
      projectType: ['', Validators.required],
      products: this.fb.control<string[]>([]),
    });
  }

  minSelectedCheckboxes(min = 1) {
    return (formArray: AbstractControl) => {
      const totalSelected = (formArray.value as boolean[]).filter((v) => v).length;
      return totalSelected >= min ? null : { required: true };
    };
  }

  toggleCategory(index: number) {
    const categoriesArray = this.step1Form.get('categories') as FormArray;
    if (categoriesArray) {
      const currentValue = categoriesArray.at(index).value;
      categoriesArray.at(index).setValue(!currentValue);
      categoriesArray.at(index).markAsDirty();
      categoriesArray.at(index).markAsTouched();
      this.categories[index].selected = !currentValue;
    }
  }

  onProductSearchChange(): void {
    if (this.productSearchTimer) {
      clearTimeout(this.productSearchTimer);
    }

    this.isProductSearchLoading = true;
    this.productSearchTimer = setTimeout(() => {
      this.resetProductPaging();
      this.isProductSearchLoading = false;
    }, 180);
  }

  toggleProduct(productId: string, checked: boolean): void {
    const id = String(productId ?? '').trim();
    if (!id) return;
    if (!checked && this.lockedProductIds.has(id)) return;

    if (checked) this.selectedProductIds.add(id);
    else this.selectedProductIds.delete(id);

    this.step1Form.patchValue({ products: Array.from(this.selectedProductIds) }, { emitEvent: false });
    this.resetProductPaging();
  }

  private resetProductPaging(): void {
    this.productPage = 0;
    this.visibleProducts = [];
    this.hasMoreProducts = false;
    this.loadProductPage(0, true);

    if (this.productsViewport) {
      this.productsViewport.scrollToIndex(0);
    }
  }

  private loadProductPage(page: number, replace = false): void {
    if (this.isProductPageLoading) return;
    this.isProductPageLoading = true;

    const query = this.vmService.queryAndMapProductsPage(
      this.productsRaw,
      {
        searchTerm: this.productSearchTerm,
        selectedProductIds: this.selectedProductIds,
        linkedProductIds: this.linkedProductIdsForCreate,
        includeLinkedOnFirstPage: !this.isEditMode,
        mappedCacheById: this.mappedProductsById,
        page,
        pageSize: this.productPageSize,
      },
      {
        projects: this.apiService.projects ?? [],
        productProjects: this.apiService.productProjects ?? [],
      }
    );

    this.visibleProducts = replace ? [...query.items] : [...this.visibleProducts, ...query.items];
    this.hasMoreProducts = query.hasMore;
    this.isProductPageLoading = false;
  }

  private sortRawProductsByModifiedDateDesc(list: dm_product[]): dm_product[] {
    return [...(list ?? [])].sort((a, b) => {
      const aTime = new Date((a as any)?.modifiedDate ?? 0).getTime();
      const bTime = new Date((b as any)?.modifiedDate ?? 0).getTime();
      return bTime - aTime;
    });
  }

  onProductsScrolled(scrolledIndex: number): void {
    if (this.isProductSearchLoading || this.isProductPageLoading || !this.hasMoreProducts) return;

    const nearEnd = scrolledIndex + 30 >= this.visibleProducts.length;
    if (!nearEnd) return;

    this.productPage += 1;
    this.loadProductPage(this.productPage, false);
  }

  private getSelectedProductIds(): string[] {
    const arr = ((this.step1Form.get('products') as FormArray)?.value as boolean[]) || [];
    return arr.map((selected, i) => (selected ? String(this.products?.[i]?.id ?? '').trim() : '')).filter(Boolean);
  }

  private sortProductsByModifiedDateDesc(list: vm_product[]): vm_product[] {
    return [...(list ?? [])].sort((a, b) => {
      const aTime = new Date(a.modifiedDate ?? 0).getTime();
      const bTime = new Date(b.modifiedDate ?? 0).getTime();
      return bTime - aTime;
    });
  }

  private loadExistingData(): void {
    if (!this.isDataLoaded) return;

    const existingData = this.projectSrvc.getProjectFormData().step1;
    if (!existingData) return;

    // EDIT MODE + locked products should be resolved here (single source of truth)
    this.isEditMode = true;
    this.lockedProductIds = this.resolveLockedProductIds(existingData);

    const projectTypePatch =
      this.projectTypes.find((pt: dm_projectType) => pt.id === existingData?.projectType?.id) || null;

    this.step1Form.patchValue({
      projectType: projectTypePatch || '',
    });

    if (existingData.categories && existingData.categories.length > 0) {
      const categoriesArray = this.step1Form.get('categories') as FormArray;
      existingData.categories.forEach((existingCat: dm_category) => {
        if (!existingCat?.selected) return;
        const categoryIndex = this.categories.findIndex((cat: vm_category) => cat.id === existingCat.id);
        if (categoryIndex !== -1) {
          categoriesArray.at(categoryIndex).setValue(true, { emitEvent: false });
          this.categories[categoryIndex].selected = true;
        }
      });
    }

    // Preselect linked products (edit + resume scenarios)
    const existingProducts = (existingData as any).products ?? [];
    this.selectedProductIds.clear();

    if (Array.isArray(existingProducts) && existingProducts.length > 0) {
      existingProducts
        .filter((p: any) => !!p?.selected)
        .forEach((p: any) => {
          const id = String(p?.id ?? '').trim();
          if (id) this.selectedProductIds.add(id);
        });
    }

    this.lockedProductIds.forEach((id) => this.selectedProductIds.add(id));
    this.step1Form.patchValue({ products: Array.from(this.selectedProductIds) }, { emitEvent: false });
  }

  loadDataForEdit(): void {
    this.isEditMode = true;
    this.loadExistingData();
  }

  getFullData() {
    const selectedIds = Array.from(this.selectedProductIds);
    const missingIds = selectedIds.filter((id) => !this.mappedProductsById.has(id));

    if (missingIds.length > 0) {
      const raw = (this.apiService.products ?? []).filter((p: any) => missingIds.includes(String(p?.id ?? '')));
      const mapped = this.vmService.mapProductData(raw);
      mapped.forEach((p) => this.mappedProductsById.set(String(p.id), p));
    }

    const selectedProducts = selectedIds
      .map((id) => this.mappedProductsById.get(id))
      .filter((p): p is vm_product => !!p)
      .map((p) => ({ ...p, selected: true }));

    return {
      ...this.step1Form.value,
      categories: this.categories.map((cat) => ({
        ...cat,
        selected: cat.selected,
      })),
      products: selectedProducts,
    };
  }

  get productsFormArray(): FormArray {
    return this.step1Form.get('products') as FormArray;
  }

  isProductChecked(productId: string): boolean {
    return this.selectedProductIds.has(String(productId ?? '').trim());
  }

  isProductLockedByExperiment(productId: string): boolean {
    const pid = String(productId ?? '').trim();
    return !!pid && this.lockedProductIds.has(pid);
  }

  getProductLockTooltip(productId: string): string {
    return this.isProductLockedByExperiment(productId) ? 'Product in use.' : '';
  }

  private resolveLockedProductIds(existingData: any): Set<string> {
    const ids = new Set<string>();

    // Preferred source from step1 payload
    const explicit = Array.isArray(existingData?.lockedProductIds) ? existingData.lockedProductIds : [];
    explicit.forEach((id: any) => {
      const v = String(id ?? '').trim();
      if (v) ids.add(v);
    });

    // Defensive fallback: derive from experiments if present in saved form object
    const allFormData = this.projectSrvc.getProjectFormData() as any;
    const experiments =
      allFormData?.experiments ?? allFormData?.step3?.experiments ?? allFormData?.project_experiments ?? [];

    (experiments || []).forEach((exp: any) => {
      const one = exp?.experimentProduct;
      const oneId = String(one?.id ?? one?.productId ?? '').trim();
      if (oneId) ids.add(oneId);

      const many = exp?.experimentProducts ?? exp?.experiment_products ?? exp?.products ?? [];
      (many || []).forEach((p: any) => {
        const pid = String(p?.id ?? p?.productId ?? '').trim();
        if (pid) ids.add(pid);
      });
    });

    return ids;
  }

  onProductRowClick(productId: string): void {
    const id = String(productId ?? '').trim();
    if (!id) return;
    const isChecked = this.isProductChecked(id);
    if (isChecked && this.isProductLockedByExperiment(id)) return;
    this.toggleProduct(id, !isChecked);
  }

  onCreateTestProductClick(): void {
    this.createTestProduct.emit();
  }

  refreshProducts(): void {
    this.productsRaw = this.sortRawProductsByModifiedDateDesc(this.apiService.products ?? []);

    this.mappedProductsById.clear();
    this.productSearchTerm = '';
    this.resetProductPaging();
  }
}
